<template>
  <Skeleton v-if="returnDetailStore.isLoading" />
  <div v-else class="grid-x">
    <div class="cell small-12 large-auto">
      <!-- We show this breadcrumb for error or new returns component-->
      <template v-if="returnDetailStore.error || componentToRender?.name !== 'ReturnDetailsView'">
        <div class="hide-for-print">
          <BreadcrumbWrapper :current-page="title" />
          <h1 v-if="!returnDetailStore.error" class="margin-top-xs" v-text="title"></h1>
          <Notification v-if="returnDetailStore.error" severity="error" class="margin-top-xs" :closable="false">
            {{ returnDetailStore.error }}
          </Notification>
        </div>
        <div
          v-for="(notification, index) in notifications"
          :key="index"
          class="margin-top-s large-margin-top-m hide-for-print"
          :class="notification.class"
        >
          <Notification :severity="notification.severity" :closable="false">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="notification.text"></div>
          </Notification>
        </div>
      </template>
      <component :is="componentToRender.component" v-if="componentToRender" />
    </div>
    <ProsWrapper
      class="hide-for-print small-12 small-border-top margin-top-s large-margin-top-m padding-top-m large-padding-top-z large-offset-2 large-1"
      data-context="PR_ZONE_A"
      :items="prosItems"
    />
  </div>
</template>
<script setup>
import { onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import logger from '@vue-util/logger';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import { sanitize } from '@vue-util/sanitize';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper.vue';
import Skeleton from '@/components/skeleton/ReturnDetails';
import { useReturnDetailStore } from '@/stores/returns/useReturnDetailStore';
import { fireApiErrorTag } from '@/utils/returns/General';
import { handleSessionTimeout } from '@/utils/SessionHandler';
import { NOTIFICATION_TYPES } from '#shared/constants';
import { RETURN_RESOLUTIONS } from 'config';
import ReturnDetailsView from '../ReturnDetailsView.vue';
import ReturnDetails from '../ReturnDetails.vue';
import ProsWrapper from '@/components/ProsWrapper.vue';

const route = useRoute();
const returnDetailStore = useReturnDetailStore();

const returnDetails = computed(() => returnDetailStore.returnDetails);

// SSR Data Fetching
const { data: ssrData } = await useAsyncData(`return-details-${route.query.pid || route.query.token}`, async () => {
  const { pid, token, rdv, orderNumber } = route.query;
  const { pidToken: pidFromState, orderToken: tokenFromState } = returnDetails.value || {};

  const hasData = (pid && pid === pidFromState) || (token && token === tokenFromState);
  if (hasData) {
    logger.info(
      `[PPE] ReturnDetailsWrapper: Skipping fetch, store already has data: ${JSON.stringify({
        pid,
        token,
      })}`,
    );
    return null;
  }
  const { cookie, 'user-agent': userAgent } = useRequestHeaders(['cookie', 'user-agent']);
  return await returnDetailStore.GET_RETURN_DETAILS({
    pendingReturnId: pid,
    token,
    rdv,
    orderNumber,
    ssrContext: { cookie, userAgent },
  });
});

const title = computed(() => {
  if (returnDetailStore.isLoading) return 'LOADING';
  if (returnDetailStore.error) return 'ERROR';
  if (!returnDetails.value.substituteShipments) return 'Return Details';
  const shipment = returnDetails.value.substituteShipments[0];
  if (shipment.type === RETURN_RESOLUTIONS.EXCHANGE) return 'Exchange Details';
  if (shipment.type === RETURN_RESOLUTIONS.REPLACEMENT) return 'Replacement Details';
  return '';
});
const prosItems = computed(() => returnDetails.value.items?.map((item) => ({ productId: item.itemDetails.productId })));

const componentToRender = computed(() => {
  if (!returnDetails.value?.substituteShipments) return { name: 'ReturnDetailsView', component: ReturnDetailsView };
  if (returnDetails.value?.substituteShipments.length) return { name: 'ReturnDetails', component: ReturnDetails };
  return undefined;
});

const showAlertMessage = computed(
  () =>
    returnDetails.value.orderReturnAlertMessage?.web?.message_type &&
    returnDetails.value.orderReturnAlertMessage?.web?.message &&
    NOTIFICATION_TYPES.includes(returnDetails.value.orderReturnAlertMessage?.web?.message_type?.toLowerCase()),
);
const sanitizedAlertMessage = computed(() => sanitize(returnDetails.value.orderReturnAlertMessage?.web?.message));
const notifications = computed(() => {
  const allNotifications = [];
  if (showAlertMessage.value) {
    allNotifications.push({
      text: sanitizedAlertMessage.value,
      severity: returnDetails.value.orderReturnAlertMessage?.web?.message_type?.toLowerCase(),
      class: 'medium-12',
    });
  }
  if (returnDetailStore.returnLabel.showNotification) {
    allNotifications.push({
      text: `Your return label has been sent to ${returnDetailStore.returnLabel.emailAddress}`,
      severity: 'success',
      class: 'medium-10',
    });
  }
  return allNotifications;
});

// CHECK ROUTE QUERY AND FETCH RETURN DETAILS
onMounted(async () => {
  fireTag('view', {
    page_name: 'return details',
    page_type: 'my account',
  });
  fireTag('link', {
    event_name: 'return details link',
    link_name: 'order track return',
  });
  if (ssrData.value) {
    fireApiErrorTag(ssrData.value);
    handleSessionTimeout(ssrData.value?.errorCode);
  }
});
onUnmounted(() => {
  returnDetailStore.CLEAR_RETURN_DETAILS();
});
</script>
